import { useModalContext } from "@wingspanhq/fe-component-library";
import { useEffect } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { ProductActivationWrapper } from "../components/ProductActivationWrapper/ProductActivationWrapper";
import { Invoicing } from "../modules/Invoicing";
import { WSQueries } from "../query/WSQuery";
import { useUserId } from "../query/hooks/helpers";
import { useFeatureFlags } from "../query/hooks/useFeatureFlags";
import { useActivities } from "../query/users/queries";
import {
  NEC_1099_PAYMENTS_SLUG,
  NEC_1099_SUMMARY_SLUG
} from "../shared/constants/nec1099";
import { useAuthorizedScopeGroups } from "../shared/utils/teamUtils";
import { DeletePayableModal } from "./components/DeletePayableModal/DeletePayableModal";
import { InvoiceActions } from "./components/InvoiceActions/InvoiceActions";
import { Payments1099CollaboratorView } from "./components/Payments1099CollaboratorView";
import { PaymentsVerificationModal } from "./components/PaymentsVerificationModal/PaymentsVerificationModal";
import { RejectPayableModal } from "./components/RejectPayableModal/RejectPayableModal";
import { TIN_VERIFICATION_MODAL } from "./components/TINVerificationModal/TINVerificationModal";
import { AddDefaultPaymentMethodWarningModal } from "./screens/AddDefaultPaymentMethodWarningModal";
import { Collaborators1099NECReview } from "./screens/Collaborators1099NECReview";
import { InvoicesClients } from "./screens/InvoicesClients";
import { InvoicesCreate } from "./screens/InvoicesCreate";
import { InvoicesDashboard } from "./screens/InvoicesDashboard";
import { InvoicesDuplicateInvoice } from "./screens/InvoicesDuplicateInvoice";
import { InvoicesDuplicateInvoiceTemplate } from "./screens/InvoicesDuplicateInvoiceTemplate";
import { InvoicesInvoiceConfirmation } from "./screens/InvoicesInvoiceConfirmation";
import { InvoicesInvoiceEdit } from "./screens/InvoicesInvoiceEdit";
import { InvoicesInvoiceTemplateConfirmation } from "./screens/InvoicesInvoiceTemplateConfirmation";
import { InvoicesInvoiceTemplateEdit } from "./screens/InvoicesInvoiceTemplateEdit";
import { InvoicesList } from "./screens/InvoicesList";
import { InvoicesListPaid } from "./screens/InvoicesListPaid";
import { InvoicesPaymentsSetup } from "./screens/InvoicesPaymentsSetup";
import { InvoicesSetupComplete } from "./screens/InvoicesSetupComplete";
import { InvoicesZeroState } from "./screens/InvoicesZeroState";
import { BulkAdjust1099AmountStep1 } from "./screens/bulkAdjust1099Amount/BulkAdjust1099AmountStep1";
import { BulkAdjust1099AmountStep2 } from "./screens/bulkAdjust1099Amount/BulkAdjust1099AmountStep2";
import { BulkUpdate1099MissingInfoStep1 } from "./screens/bulkAdjust1099MissingInfo/BulkUpdate1099MissingInfoStep1";
import { BulkUpdate1099MissingInfoStep2 } from "./screens/bulkAdjust1099MissingInfo/BulkUpdate1099MissingInfoStep2";
import { BulkUploadCollaboratorsStep1 } from "./screens/bulkUploadCollaborators/BulkUploadCollaboratorsStep1";
import { BulkUploadCollaboratorsStep2 } from "./screens/bulkUploadCollaborators/BulkUploadCollaboratorsStep2";
import { InvoicesCollaboratorsIndex } from "./screens/collaborators/InvoicesCollaboratorsIndex";
import { DeductionsIndex } from "./screens/deductions/DeductionsIndex";
import { AddDeduction } from "./screens/deductions/modals/AddDeduction";
import { DeductionInfo } from "./screens/deductions/modals/DeductionsInfo";
import { DeleteDeduction } from "./screens/deductions/modals/DeleteDeduction";
import { Payables } from "./screens/payables";
import { BulkUploadPayablesInfo } from "./screens/payables/bulkUploadPayables/BulkUploadPayablesInfo";
import { BulkUploadPayablesStatus } from "./screens/payables/bulkUploadPayables/BulkUploadPayablesStatus";

export const Invoices = () => {
  const featureFlagsQuery = useFeatureFlags();
  const history = useHistory();
  const { openModal } = useModalContext();
  const userId = useUserId();
  const activityQuery = useActivities(userId);

  useEffect(() => {
    // Parse url params on component mount
    const urlSearchParams = new URLSearchParams(window.location.search);
    const verifyTinParam = urlSearchParams.get("verify_tin");

    if (verifyTinParam) {
      openModal(TIN_VERIFICATION_MODAL);
      history.push("/member/invoices");
    }
  }, []);

  const {
    isRequestingUserSessionExists,
    isAdminSessionExists
  } = useAuthorizedScopeGroups();

  if (!featureFlagsQuery.data) {
    return null;
  }

  return (
    <>
      <PaymentsVerificationModal />
      <AddDefaultPaymentMethodWarningModal />
      <InvoiceActions />
      <DeletePayableModal />
      <RejectPayableModal />
      <AddDeduction />
      <DeductionInfo />
      <DeleteDeduction />

      <Switch>
        <Route
          path="/member/invoices/set-up"
          component={InvoicesPaymentsSetup}
        />

        <Route
          path="/member/invoices/complete"
          exact
          component={InvoicesSetupComplete}
        />

        <Route
          path="/member/invoices/contacts/clients"
          component={InvoicesClients}
        />

        <Route
          path={`*/:memberClientId/${NEC_1099_SUMMARY_SLUG}/:year/${NEC_1099_PAYMENTS_SLUG}`}
          component={Payments1099CollaboratorView}
        />

        {/* TODO: These routes are out dated and should be removed */}
        <Route
          path="/member/invoices/contacts/collaborators/1099/adjust-compensation/download"
          component={BulkAdjust1099AmountStep1}
        />

        <Route
          path="/member/invoices/contacts/collaborators/1099/adjust-compensation/upload"
          component={BulkAdjust1099AmountStep2}
        />

        <Route
          path="/member/invoices/contacts/collaborators/1099/missing-information/download"
          component={BulkUpdate1099MissingInfoStep1}
        />

        <Route
          path="/member/invoices/contacts/collaborators/1099/missing-information/upload"
          component={BulkUpdate1099MissingInfoStep2}
        />

        <Route
          path="/member/invoices/contacts/collaborators/1099"
          component={Collaborators1099NECReview}
        />
        {/* The above should be deprecated */}

        <Route
          path="/member/invoices/contacts/collaborators/bulk-create/info"
          component={BulkUploadCollaboratorsStep1}
        />
        <Route
          path={
            featureFlagsQuery.data?.bulkImporterCollaborator
              ? "/member/invoices/contacts/collaborators/bulk-create/:bulkBatchId/upload"
              : "/member/invoices/contacts/collaborators/bulk-create/upload"
          }
          component={BulkUploadCollaboratorsStep2}
        />
        <Route
          path="/member/invoices/contacts/collaborators/:collaboratorId/deductions"
          component={DeductionsIndex}
        />

        <Route
          path="/member/invoices/contacts/collaborators"
          component={InvoicesCollaboratorsIndex}
        />

        <Route
          path="/member/invoices/payables/bulk-upload/info"
          component={BulkUploadPayablesInfo}
        />
        <Route
          path="/member/invoices/payables/bulk-upload/:bulkBatchId/status"
          component={BulkUploadPayablesStatus}
        />

        <Route path="/member/invoices/payables" component={Payables} />

        <ProductActivationWrapper>
          <WSQueries
            queries={{
              activityQuery
            }}
            isPage
          >
            {({ activityQuery: { data: activity } }) =>
              activity.flows.paymentsSetup?.complete ? (
                featureFlagsQuery.data?.invoicesRevampM1 ? (
                  <Switch>
                    <Route path="/member/invoices" component={Invoicing} />
                  </Switch>
                ) : (
                  <Switch>
                    <Route
                      path="/member/invoices/create"
                      exact
                      component={InvoicesCreate}
                    />

                    <Route
                      path="/member/invoices/list"
                      component={InvoicesList}
                    />

                    <Route
                      path="/member/invoices/paid"
                      component={InvoicesListPaid}
                    />

                    <Route
                      path="/member/invoices/:invoiceId/edit"
                      component={InvoicesInvoiceEdit}
                    />
                    <Route
                      path="/member/invoices/:invoiceId/duplicate"
                      exact
                      component={InvoicesDuplicateInvoice}
                    />
                    <Route
                      path="/member/invoices/template/:invoiceTemplateId/edit"
                      component={InvoicesInvoiceTemplateEdit}
                    />
                    <Route
                      path="/member/invoices/template/:invoiceTemplateId/duplicate"
                      exact
                      component={InvoicesDuplicateInvoiceTemplate}
                    />
                    <Route
                      path="/member/invoices/:invoiceId/send-success"
                      exact
                      component={InvoicesInvoiceConfirmation}
                    />
                    <Route
                      path="/member/invoices/template/:invoiceTemplateId/send-success"
                      exact
                      component={InvoicesInvoiceTemplateConfirmation}
                    />
                    <Route
                      path="/member/invoices"
                      component={InvoicesDashboard}
                    />
                    <Route path="/member/invoices" component={Invoicing} />
                  </Switch>
                )
              ) : isRequestingUserSessionExists || isAdminSessionExists ? (
                <Switch>
                  <Route
                    path="/member/invoices/paid"
                    component={InvoicesListPaid}
                  />
                  <Route
                    path="/member/invoices/*"
                    component={InvoicesZeroState}
                  />
                  <Route
                    path="/member/invoices"
                    component={InvoicesZeroState}
                  />
                </Switch>
              ) : (
                <InvoicesZeroState />
              )
            }
          </WSQueries>
        </ProductActivationWrapper>
      </Switch>
    </>
  );
};
